import styled from "@emotion/styled";
import { ArrowLeftIcon } from "lucide-react";
import { useCallback } from "react";
import { FREE_TEXT_COPY } from "../../../constants/feedback-comments";
import { colors, radius, spacing } from "../../design-tokens";
import { ActionButton } from "../../primitives/action-button";

const TextAreaContainer = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${spacing.lg}px;
  box-sizing: border-box;

  border: 1px solid ${colors.border};
  border-radius: ${radius.md}px;
  padding: ${spacing.lg}px;
`;

const BackHeader = styled.div`
  display: flex;
  align-items: center;
  gap: ${spacing.md}px;
  color: ${colors.text};
`;

const BackButton = styled(ActionButton)`
  padding: ${spacing.xs}px;
`;

const BackText = styled.span`
  font-size: 16px;
  font-weight: 500;
  color: ${colors.text};
`;

const TextArea = styled.textarea`
  width: 100%;
  height: 100px;
  padding: ${spacing.md}px;
  border: 1px solid ${colors.mutedText};
  border-radius: ${radius.md}px;
  background: ${colors.background};
  color: ${colors.text};
  font-size: 16px;
  font-family: inherit;
  resize: vertical;
  box-sizing: border-box;
  line-height: 1.5;

  &::-webkit-resizer {
    display: none;
  }

  &::placeholder {
    color: ${colors.textPlaceholder};
  }

  &:focus {
    outline: none;
    border-color: ${colors.textSecondary};
  }
`;

const ButtonContainer = styled.div`
  display: flex;
  justify-content: flex-end;
`;

const SubmitButton = styled(ActionButton)`
  /* Primary button style will be handled by variant */
`;

type FreeTextInputProps = {
  readonly isSubmitting: boolean;
  readonly onBack?: () => void;
  readonly onSubmit: (text: string) => void;
  readonly onChange: (text: string) => void;
  readonly showBack?: boolean;
  readonly value: string;
};

export function FreeTextInput({ isSubmitting, onBack, onChange, onSubmit, showBack = true, value }: FreeTextInputProps) {
  const handleSubmit = useCallback(() => {
    if (!value.trim() || isSubmitting)
      return;
    onSubmit(value.trim());
  }, [isSubmitting, onSubmit, value]);

  return (
    <TextAreaContainer>
      <BackHeader>
        {showBack && (
          <BackButton
            variant="ghost"
            onClick={onBack}
            aria-label="Go back to comment options"
            disabled={isSubmitting}
          >
            <ArrowLeftIcon size={20} strokeWidth={2} color={colors.text} />
          </BackButton>
        )}
        <BackText>{FREE_TEXT_COPY}</BackText>
      </BackHeader>
      <TextArea
        placeholder="Add specific details"
        value={value}
        onChange={e => onChange(e.target.value)}
        disabled={isSubmitting}
      />
      <ButtonContainer>
        <SubmitButton
          variant="primary"
          onClick={handleSubmit}
          disabled={isSubmitting || !value.trim()}
        >
          Submit
        </SubmitButton>
      </ButtonContainer>
    </TextAreaContainer>
  );
}
